import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiForm } from "../api/client";
import { invalidateAfter, keys } from "../api/query-registry";

export interface RotaDay {
  id: string;
  date: string;
  month: string;
  homeId: string;
  startTime: string;
  endTime: string;
  marker: string | null;
  source: string;
}

export interface RotaSummary {
  month: string;
  perHome: { homeId: string; name: string; days: number }[];
  restDays: string[];
}

export interface ImportPreviewItem {
  date: string;
  homeName?: string;
  homeId?: string;
  from?: { startTime: string; endTime: string; marker: string | null };
  to?: { startTime: string; endTime: string; marker: string };
  id?: string;
}

export interface RotaImportResult {
  month: string;
  commit: boolean;
  summary: {
    added: number;
    changed: number;
    removed: number;
    unchanged: number;
    skippedManual: number;
    unreadDays: string[];
    unknownHomes: string[];
    fileName: string;
  };
  preview: {
    added: ImportPreviewItem[];
    changed: ImportPreviewItem[];
    removed: ImportPreviewItem[];
    unchanged: ImportPreviewItem[];
    skippedManual: ImportPreviewItem[];
    unreadDays: string[];
  };
}

export function useRota(month: string) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: keys.rotaList(month),
    queryFn: () => api<{ month: string; days: RotaDay[] }>(`/api/rota?month=${month}`),
    staleTime: 30_000,
  });
  const summary = useQuery({
    queryKey: keys.rotaSummary(month),
    queryFn: () => api<RotaSummary>(`/api/rota/summary?month=${month}`),
    staleTime: 30_000,
  });
  const imports = useQuery({
    queryKey: keys.rotaImports(month),
    queryFn: () => api<{ id: string; fileName: string; importedAt: string; summary: unknown }[]>(`/api/rota/imports?month=${month}`),
    staleTime: 60_000,
  });

  const invalidate = () => invalidateAfter(qc, "rotaChanged");

  const dryRun = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return apiForm<RotaImportResult>("/api/rota/import", fd);
    },
  });
  const commit = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return apiForm<RotaImportResult>("/api/rota/import?commit=true", fd);
    },
    onSuccess: invalidate,
  });
  const createShift = useMutation({
    mutationFn: (p: { date: string; homeId: string; startTime: string; endTime: string; marker?: string | null }) =>
      api<RotaDay>("/api/rota/shifts", { method: "POST", body: p }),
    onSuccess: invalidate,
  });
  const updateShift = useMutation({
    mutationFn: ({ id, ...p }: { id: string; startTime: string; endTime: string; marker?: string | null }) =>
      api<RotaDay>(`/api/rota/shifts/${id}`, { method: "PUT", body: p }),
    onSuccess: invalidate,
  });
  const deleteShift = useMutation({
    mutationFn: (id: string) => api<void>(`/api/rota/shifts/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  return { list, summary, imports, dryRun, commit, createShift, updateShift, deleteShift };
}

export function shiftMonth(date: Date, delta: number): string {
  const d = new Date(date.getFullYear(), date.getMonth() + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
